/**
 * The search box's colour layer (#2290): the text with each filter token
 * wrapped in spans coloured by kind and status. Only colour, background,
 * box-shadow, outline and decoration may differ from plain text, so the
 * layer lines up glyph for glyph with the input above it.
 */
import type { ReactNode } from 'react';
import { keyOf, takesGroups, type Token } from './searchSyntax';

/** How a token looks: being typed and not yet right reads as unfinished, not wrong. */
export function displayStatus(t: Token): string {
  if (t.active) return t.status === 'valid' ? 'active' : 'incomplete';
  return t.status;
}

/**
 * A value with its operators (`+` and `|` outside quotes, #2299) in spans of
 * their own, so "and" and "or" read apart from the names.
 */
export function valueParts(value: string, ops: string): { text: string; op: boolean }[] {
  const out: { text: string; op: boolean }[] = [];
  let quoted = false;
  let run = '';
  for (let i = 0; i < value.length; i++) {
    const c = value[i];
    if (quoted && c === '\\' && i + 1 < value.length) { run += c + value[i + 1]; i++; continue; }
    if (c === '"') quoted = !quoted;
    if (!quoted && ops.includes(c)) {
      if (run) out.push({ text: run, op: false });
      out.push({ text: c, op: true });
      run = '';
    } else {
      run += c;
    }
  }
  if (run) out.push({ text: run, op: false });
  return out;
}

/** The mirror's content: the text, with each filter token in its colours. */
export function renderHighlight(text: string, tokens: Token[]): ReactNode[] {
  const out: ReactNode[] = [];
  let at = 0;
  for (const t of tokens) {
    if (t.start > at) out.push(text.slice(at, t.start));
    if (t.type === 'word') {
      out.push(<span key={t.start} className="dsq-word">{t.raw}</span>);
    } else {
      const k = keyOf(t.raw);
      const split = k?.valueStart ?? t.raw.length;
      let ops = '';
      if (k && takesGroups(k.key)) ops = '+|';
      else if (k?.key === 'tag') ops = '|';
      const value = t.raw.slice(split);
      out.push(
        <span
          key={t.start}
          className={`dsq-tok dsq-k-${t.kind} is-${displayStatus(t)}`}
          data-status={displayStatus(t)}
        >
          <span className="dsq-tok-key">{t.raw.slice(0, split)}</span>
          {split < t.raw.length && (
            <span className="dsq-tok-val">
              {ops ? valueParts(value, ops).map((p, i) => (p.op ? (
                <span
                  key={i} // NOSONAR typescript:S6479 false-positive: the parts are re-split from the input text on every render; each is a stateless span in an aria-hidden mirror, and operators repeat
                  className="dsq-tok-op"
                >{p.text}</span>
              ) : p.text)) : value}
            </span>
          )}
        </span>,
      );
    }
    at = t.end;
  }
  if (at < text.length) out.push(text.slice(at));
  return out;
}
